import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Facebook, Twitter, Linkedin, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import logo from '../assets/logo.png';

const Footer = () => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const { t, isRTL } = useLanguage();

  const handleLegalNavigation = (path) => {
    navigate(`/${lang || 'en'}${path}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectionNavigation = (sectionId) => {
    // Check if we're on the home page
    const currentPath = window.location.pathname;
    const isHomePage = currentPath === `/${lang}` || currentPath === `/${lang}/`;

    if (isHomePage) {
      // If on home page, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If not on home page, navigate to home then scroll
      navigate(`/${lang || 'en'}`);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">

            <div className="flex items-center gap-0 mb-4">
              {isRTL ? (
                <>
                  <span className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                    didoo
                  </span>
                  <img src={logo} alt="Adidoo Logo" className="w-10 h-10" />
                </>
              ) : (
                <>
                  <img src={logo} alt="Adidoo Logo" className="w-10 h-10" />
                  <span className="text-3xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                    didoo
                  </span>
                </>
              )}
            </div>


            <p className="text-gray-400 mb-6 max-w-md">{t.footer.tagline}</p>

            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-teal-400" />
                <a href={`mailto:${t.contact.info.email}`} className="hover:text-teal-400 transition-colors">
                  {t.contact.info.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-teal-400" />
                <a href={`tel:${t.contact.info.phone}`} className="hover:text-teal-400 transition-colors" dir="ltr">
                  {t.contact.info.phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-teal-400" />
                <span>{t.contact.info.address}</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {[
                { Icon: Facebook, link: 'https://facebook.com' },
                { Icon: Twitter, link: 'https://twitter.com' },
                { Icon: Linkedin, link: 'www.linkedin.com/in/adidoo-digital-transformation-partner-a9541b3a7' },
                { Icon: Instagram, link: 'https://instagram.com' }
              ].map(({ Icon, link }, index) => (
                <a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gradient-to-r hover:from-teal-500 hover:to-cyan-500 flex items-center justify-center transition-all duration-300 hover:scale-110"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-bold text-white mb-4">{t.footer.company.title}</h3>
            <ul className="space-y-2">
              {[
                { label: t.footer.company.links[0], action: () => handleSectionNavigation('hero') },
                { label: t.footer.company.links[1], action: () => handleSectionNavigation('services') },
                { label: t.footer.company.links[2], action: () => handleSectionNavigation('portfolio') },
                { label: t.footer.company.links[3], action: () => handleSectionNavigation('team') }
              ].map((link, index) => (
                <li key={index}>
                  <button
                    onClick={link.action}
                    className="hover:text-teal-400 transition-colors text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="font-bold text-white mb-4">{t.footer.services.title}</h3>
            <ul className="space-y-2">
              {t.footer.services.links.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleSectionNavigation('services')}
                    className="hover:text-teal-400 transition-colors text-sm text-left"
                  >
                    {link}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="font-bold text-white mb-4">{t.footer.support.title}</h3>
            <ul className="space-y-2">
              {[
                { label: t.footer.support.links[0], action: () => handleLegalNavigation('/contact') },
                { label: t.footer.support.links[1], action: () => handleSectionNavigation('blog') },
                { label: t.footer.support.links[2], action: () => handleLegalNavigation('/contact') }
              ].map((link, index) => (
                <li key={index}>
                  <button
                    onClick={link.action}
                    className="hover:text-teal-400 transition-colors text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-400">{t.footer.copyright}</div>
            <div className="flex gap-6 text-sm">
              <button
                onClick={() => handleLegalNavigation('/privacy-policy')}
                className="hover:text-teal-400 transition-colors"
              >
                {t.footer.legal.links[0]}
              </button>
              <button
                onClick={() => handleLegalNavigation('/terms-of-service')}
                className="hover:text-teal-400 transition-colors"
              >
                {t.footer.legal.links[1]}
              </button>
              <button
                onClick={() => handleLegalNavigation('/cookie-policy')}
                className="hover:text-teal-400 transition-colors"
              >
                {t.footer.legal.links[2]}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;